import React, { type AnimationEvent, useEffect, useState } from 'react'
import { IconButton } from './IconButton'
import classNames from 'classnames'
import type { ToastInfo } from './Toaster'

type ToastColor = 'success' | 'error' | 'warning' | 'info' | 'default'

type Props = Omit<ToastInfo, 'id'>

const defaults = {
    duration: 3000,
    delay: 0,
    color: 'default' as ToastColor,
}

export function Toast({
    message,
    icon,
    duration,
    delay,
    color,
    onClose,
    onClick,
    actionButtonText,
    hideDismissButton,
}: Props) {
    duration ??= defaults.duration
    delay ??= defaults.delay
    color ??= defaults.color

    const [removing, setRemoving] = useState(false)
    const [added, setAdded] = useState(false)

    useEffect(() => {
        const durationTimer = setTimeout(startRemove, duration)
        const delayTimer = setTimeout(add, delay)
        return () => {
            clearTimeout(durationTimer)
            clearTimeout(delayTimer)
        }
    }, [])

    const startRemove = () => setRemoving(true)
    const add = () => setAdded(true)
    const handleAnimationEnd = ({ animationName }: AnimationEvent) =>
        animationName === 'AppCore__ui__Toast_slideOut' ? onClose() : null

    let colorClass: ToastColor
    switch (color) {
        case 'success':
            colorClass = 'success'
            break
        case 'error':
            colorClass = 'error'
            break
        case 'warning':
            colorClass = 'warning'
            break
        case 'info':
            colorClass = 'info'
            break
        default:
            colorClass = 'default'
            break
    }

    return added ? (
        <div
            className={`${classNames('AppCore__ui__Toast', removing && 'AppCore__ui__Toast_removing')} ${colorClass}`}
            onAnimationEnd={handleAnimationEnd}
        >
            <div
                onClick={() => {
                    onClick && onClick()
                }}
            >
                <zaptic-icon icon-name={icon} />
                {message}

                {actionButtonText && <p className="button">{actionButtonText}</p>}
            </div>
            {!hideDismissButton && (
                <IconButton onClick={startRemove} iconName="cross" size="small" border={false} background={false} />
            )}
        </div>
    ) : null
}
